import { useState } from "preact/hooks";
import { Home, Library, Waves, TrendingUp } from "lucide-preact";
import { Panel, Button, Chip, Ring, Meter, Counter, Sheet, TabBar } from "../components";
import { themePref, setThemePref } from "../lib/theme";
import type { TabItem } from "../components";

const TABS: TabItem[] = [
  { id: "today", label: "Today", icon: Home },
  { id: "library", label: "Library", icon: Library },
  { id: "map", label: "Map", icon: Waves },
  { id: "progress", label: "Progress", icon: TrendingUp },
];

/** Dev-only route (/__kitchen) — every primitive, both themes. Not shipped in the tab flow. */
export function KitchenSink() {
  const [activeTab, setActiveTab] = useState("today");
  const [activeChip, setActiveChip] = useState("daily");
  const [meterValue, setMeterValue] = useState(0.4);
  const [counterValue, setCounterValue] = useState(1280);
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div class="app-shell u-scanlines" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <h1 class="display" style={{ fontSize: "var(--t-title)" }}>
        Kitchen Sink
      </h1>

      <section style={{ display: "flex", gap: 8 }}>
        <span class="label" style={{ alignSelf: "center", marginRight: 8 }}>
          Theme
        </span>
        {(["system", "dark", "light"] as const).map((p) => (
          <Chip key={p} active={themePref.value === p} onClick={() => setThemePref(p)}>
            {p}
          </Chip>
        ))}
      </section>

      <section style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="primary" size="sm">
          Primary sm
        </Button>
        <Button variant="primary" size="md">
          Primary md
        </Button>
        <Button variant="primary" size="lg" breathing>
          Primary lg breathing
        </Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="quiet">Quiet</Button>
        <Button variant="primary" disabled>
          Disabled
        </Button>
      </section>

      <section style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["daily", "twisters", "fluency", "weak sounds", "free play"].map((c) => (
          <Chip key={c} active={activeChip === c} onClick={() => setActiveChip(c)}>
            {c}
          </Chip>
        ))}
      </section>

      <section style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
        <Ring segments={7} filled={4} current={4} size={100} centerLabel="4/7" />
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <Meter value={meterValue} targetMin={0.6} targetMax={0.85} height={140} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Button size="sm" variant="ghost" onClick={() => setMeterValue(Math.random())}>
              Randomize meter
            </Button>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Counter value={counterValue} minDigits={4} style={{ fontSize: "2rem" }} />
          <Button size="sm" variant="ghost" onClick={() => setCounterValue((v) => v + 40)}>
            +40
          </Button>
        </div>
      </section>

      <section style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Panel style={{ width: 220 }}>
          <p class="label">Panel</p>
          <p class="display" style={{ fontSize: "var(--t-lead)" }}>
            She sells seashells
          </p>
        </Panel>
        <Panel raised style={{ width: 220 }}>
          <p class="label">Panel raised</p>
          <p class="mono" style={{ fontSize: "var(--t-lg)" }}>
            140 BPM
          </p>
        </Panel>
        <Button variant="ghost" onClick={() => setSheetOpen(true)}>
          Open sheet
        </Button>
      </section>

      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} labelledBy="sheet-title">
        <h2 id="sheet-title" class="display" style={{ fontSize: "var(--t-title)", marginBottom: 12 }}>
          A sheet
        </h2>
        <p style={{ color: "var(--fg-muted)", marginBottom: 16 }}>
          Bottom sheet on mobile, centered modal on desktop. Escape or the backdrop closes it.
        </p>
        <Button onClick={() => setSheetOpen(false)}>Close</Button>
      </Sheet>

      <TabBar items={TABS} activeId={activeTab} onSelect={setActiveTab} />
    </div>
  );
}
